import { useState, useCallback, useRef } from 'react';
import { message } from 'antd';
import wsuiApi from '@api/wsuiApi';
import sdk from '@baiducloud/sdk';

export interface ImageItem {
    id: string;
    file: File;
    url: string; // 本地预览 URL (objectURL)
    uploadStatus: 'pending' | 'uploading' | 'success' | 'failed';
    /** 上传后的远程 URL（origin 尺寸） */
    uploadedUrl?: string;
    /** 缩略图 URL */
    iconUrl?: string;
    /** 小图 URL */
    smallUrl?: string;
    /** BOS 对象名（用于消息发送时的 value 字段） */
    fileName?: string;
}

const MAX_COUNT = 5;
const MAX_SIZE = 10 * 1024 * 1024; // 10MB
const ACCEPTED_TYPES = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif'];
const ACCEPTED_EXTENSIONS = ['.jpg', '.jpeg', '.png', '.gif'];

let imageIdCounter = 0;
function generateImageId(): string {
    return `img_${Date.now()}_${++imageIdCounter}`;
}

// 上传队列，确保不会并发请求 BOS token
let uploadQueue: Promise<any> = Promise.resolve();
function enqueueUpload(files: File[]) {
    const task = uploadQueue.then(() => uploadFilesToBos(files));
    uploadQueue = task.catch(() => {});
    return task;
}

/**
 * 获取文件扩展名
 */
function getFileExtension(file: File): string {
    // 优先从文件名提取
    if (file.name && file.name.includes('.')) {
        return '.' + (file.name.split('.').pop()?.toLowerCase() || 'png');
    }
    // 从 MIME type 中提取
    if (file.type && file.type.includes('/')) {
        return '.' + (file.type.split('/')[1] || 'png');
    }
    return '.png';
}

/**
 * 批量上传多个文件到 BOS
 * 参照 vita/pages-doctor-claw-im 的 uploadFileToBos 实现
 */
async function uploadFilesToBos(
    files: File[]
): Promise<Array<{ origin: string; icon: string; small: string; fileName: string }>> {
    const fileExtensions = files.map((f) => getFileExtension(f));

    // 获取 BOS STS token，入参格式参照 xuqiu.md
    const { data: tokenData } = await wsuiApi.getBucketToken({
        bucketConfName: 'doctoragent',
        fileNum: files.length,
        fileExtensions,
        businessType: 'doctor_youyi',
        appKey: 'pc_wenzhen',
    });

    const { accessKeyID, secretAccessKey, sessionToken, bucket, fileNames = [], mapFileUrls = {} } = tokenData || {};

    if (!accessKeyID || !bucket) {
        throw new Error('获取 BOS 上传凭证失败');
    }

    const BosClient = sdk.BosClient;
    const client = new BosClient({
        credentials: {
            ak: accessKeyID,
            sk: secretAccessKey,
        },
        sessionToken,
        endpoint: tokenData.endPoint,
    });

    // 并行上传所有文件
    const results = await Promise.all(
        files.map(async (file, index) => {
            const objectId = fileNames[index];
            const fileExtension = file.name?.split(".").pop()?.toLowerCase();
            const contentType = sdk.MimeType.guess(fileExtension);

            await client.putObjectFromBlob(bucket, objectId, file, {
                "Content-Type": contentType,
            });

            // 从 mapFileUrls 中取真实 CDN URL
            const fileUrlInfo = mapFileUrls?.[objectId];
            const origin = fileUrlInfo?.origin || "";
            const icon = fileUrlInfo?.icon || origin;
            const small = fileUrlInfo?.small || origin;

            return { origin, icon, small, fileName: fileNames[index] };
        })
    );

    return results;

}

export function useImageUpload() {
    const [imageList, setImageList] = useState<ImageItem[]>([]);
    const fileInputRef = useRef<HTMLInputElement | null>(null);
    const imageCountRef = useRef(0);

    const isMaxCount = imageList.length >= MAX_COUNT;

    const isValidFormat = useCallback((file: File): boolean => {
        if (ACCEPTED_TYPES.includes(file.type)) return true;
        const ext = '.' + file.name.split('.').pop()?.toLowerCase();
        return ACCEPTED_EXTENSIONS.includes(ext);
    }, []);

    const isValidSize = useCallback((file: File): boolean => {
        return file.size <= MAX_SIZE;
    }, []);

    /**
     * 上传单张图片并更新状态（用于重试）
     */
    const uploadSingleImage = useCallback(async (item: ImageItem) => {
        // 标记为上传中
        setImageList((prev) =>
            prev.map((img) => (img.id === item.id ? { ...img, uploadStatus: 'uploading' as const } : img))
        );

        try {
            const results = await uploadFilesToBos([item.file]);
            const urls = results[0];
            setImageList((prev) =>
                prev.map((img) =>
                    img.id === item.id
                        ? {
                              ...img,
                              uploadStatus: 'success' as const,
                              uploadedUrl: urls.origin,
                              iconUrl: urls.icon,
                              smallUrl: urls.small,
                              fileName: urls.fileName,
                          }
                        : img
                )
            );
        } catch (error) {
            console.error('图片上传失败:', error);
            setImageList((prev) =>
                prev.map((img) => (img.id === item.id ? { ...img, uploadStatus: 'failed' as const } : img))
            );
        }
    }, []);

    /**
     * 处理文件添加（统一入口）
     * 参照 vita/pages-doctor-claw-im 的 uploadFileToBos 流程
     */
    const addImages = useCallback(
        (files: File[]) => {
            if (files.length === 0) return;

            // 格式过滤
            const validFormatFiles: File[] = [];
            let hasInvalidFormat = false;
            for (const file of files) {
                if (isValidFormat(file)) {
                    validFormatFiles.push(file);
                } else {
                    hasInvalidFormat = true;
                }
            }
            if (hasInvalidFormat && validFormatFiles.length > 0) {
                message.warning('部分文件非有效图片，已自动过滤');
            } else if (hasInvalidFormat && validFormatFiles.length === 0) {
                message.warning('仅支持图片文件');
                return;
            }

            // 大小过滤
            const validSizeFiles: File[] = [];
            let hasOversized = false;
            for (const file of validFormatFiles) {
                if (isValidSize(file)) {
                    validSizeFiles.push(file);
                } else {
                    hasOversized = true;
                }
            }
            if (hasOversized && validSizeFiles.length > 0) {
                message.warning('部分图片大小超出10MB，已自动过滤');
            } else if (hasOversized && validSizeFiles.length === 0) {
                message.warning('图片大小超出10MB');
                return;
            }

            // 数量限制（使用 ref 获取实时数量，避免闭包过期）
            const currentCount = imageCountRef.current;
            const remaining = MAX_COUNT - currentCount;
            if (remaining <= 0) {
                message.warning('最多上传5张图片');
                return;
            }

            const filesToAdd = validSizeFiles.slice(0, remaining);
            if (validSizeFiles.length > remaining) {
                message.warning('最多上传5张图片');
            }

            // 创建新图片项，初始状态为 uploading
            const newItems: ImageItem[] = filesToAdd.map((file) => ({
                id: generateImageId(),
                file,
                url: URL.createObjectURL(file),
                uploadStatus: 'uploading' as const,
            }));

            // 先将图片添加到列表（状态为 uploading）
            imageCountRef.current += filesToAdd.length;
            setImageList((prev) => [...prev, ...newItems]);

            // 发起批量上传
            const itemIds = newItems.map((item) => item.id);
            const itemFiles = newItems.map((item) => item.file);

            enqueueUpload(itemFiles)
                .then((results) => {
                    setImageList((current) =>
                        current.map((img) => {
                            const itemIndex = itemIds.indexOf(img.id);
                            if (itemIndex !== -1) {
                                const urls = results[itemIndex];
                                return {
                                    ...img,
                                    uploadStatus: 'success' as const,
                                    uploadedUrl: urls.origin,
                                    iconUrl: urls.icon,
                                    smallUrl: urls.small,
                                    fileName: urls.fileName,
                                };
                            }
                            return img;
                        })
                    );
                })
                .catch(() => {
                    setImageList((current) =>
                        current.map((img) =>
                            itemIds.includes(img.id) ? { ...img, uploadStatus: 'failed' as const } : img
                        )
                    );
                });
        },
        [isValidFormat, isValidSize]
    );

    /**
     * 删除单张图片
     */
    const removeImage = useCallback((id: string) => {
        setImageList((prev) => {
            const item = prev.find((img) => img.id === id);
            if (item) {
                URL.revokeObjectURL(item.url);
            }
            const next = prev.filter((img) => img.id !== id);
            imageCountRef.current = next.length;
            return next;
        });
    }, []);

    /**
     * 清空所有图片
     */
    const clearImages = useCallback(() => {
        setImageList((prev) => {
            prev.forEach((img) => URL.revokeObjectURL(img.url));
            return [];
        });
        imageCountRef.current = 0;
    }, []);

    /**
     * 点击图片 icon 触发文件选择
     */
    const triggerFileSelect = useCallback(() => {
        if (imageList.length >= MAX_COUNT) {
            message.warning('最多上传5张图片');
            return;
        }
        fileInputRef.current?.click();
    }, [imageList.length]);

    /**
     * 文件选择器 onChange
     */
    const handleFileInputChange = useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
            const files = Array.from(e.target.files || []);
            if (files.length > 0) {
                addImages(files);
            }
            if (e.target) {
                e.target.value = '';
            }
        },
        [addImages]
    );

    /**
     * 处理粘贴事件中的图片
     */
    const handlePasteImages = useCallback(
        (clipboardData: DataTransfer): boolean => {
            const files: File[] = [];
            for (let i = 0; i < clipboardData.items.length; i++) {
                const item = clipboardData.items[i];
                if (item.type.startsWith('image/')) {
                    const file = item.getAsFile();
                    if (file) files.push(file);
                }
            }
            if (files.length > 0) {
                addImages(files);
                return true;
            }
            return false;
        },
        [addImages]
    );

    /**
     * 处理拖拽文件
     */
    const handleDropFiles = useCallback(
        (dataTransfer: DataTransfer) => {
            const files = Array.from(dataTransfer.files);
            if (files.length > 0) {
                addImages(files);
            }
        },
        [addImages]
    );

    /**
     * 是否所有图片都上传完成
     */
    const isAllUploaded = imageList.length > 0 && imageList.every((img) => img.uploadStatus === 'success');

    /**
     * 是否有正在上传的图片
     */
    const isUploading = imageList.some((img) => img.uploadStatus === 'uploading' || img.uploadStatus === 'pending');

    return {
        imageList,
        isMaxCount,
        isAllUploaded,
        isUploading,
        fileInputRef,
        addImages,
        removeImage,
        clearImages,
        retryImage: uploadSingleImage,
        triggerFileSelect,
        handleFileInputChange,
        handlePasteImages,
        handleDropFiles,
    };
}
